import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Icons } from './Icons';
import { LogEntryRow } from './LogEntryRow';
import { searchLogs, getAppStatus, LogEntry } from '../services/api';

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'UNKNOWN';

interface AppStatus {
  name: string;
  stats: {
    totalLogs: number;
    oldestLog?: string;
    newestLog?: string;
    databaseSize: number;
  } | null;
}

interface LogPaneProps {
  appName: string;
  onClose: () => void;
}

// Common timezones for quick selection
const COMMON_TIMEZONES = [
  { label: 'UTC', value: 'UTC' },
  { label: 'Local', value: Intl.DateTimeFormat().resolvedOptions().timeZone },
  { label: 'US Eastern', value: 'America/New_York' },
  { label: 'US Pacific', value: 'America/Los_Angeles' },
  { label: 'UK', value: 'Europe/London' },
  { label: 'Central Europe', value: 'Europe/Berlin' },
  { label: 'India', value: 'Asia/Kolkata' },
  { label: 'Singapore', value: 'Asia/Singapore' },
  { label: 'Australia East', value: 'Australia/Sydney' },
];

const LEVEL_BADGES: Record<LogLevel, string> = {
  ERROR: 'badge badge-error',
  WARN: 'badge badge-warn',
  INFO: 'badge badge-info',
  DEBUG: 'badge badge-debug',
  UNKNOWN: 'badge badge-debug',
};

export function LogPane({ appName, onClose }: LogPaneProps) {
  // App status
  const [status, setStatus] = useState<AppStatus | null>(null);

  // Mode & Controls
  const [isLiveMode, setIsLiveMode] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);

  // Live logs
  const [liveLogs, setLiveLogs] = useState<LogEntry[]>([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveHasMore, setLiveHasMore] = useState(true);
  const wsRef = useRef<WebSocket | null>(null);
  const [wsConnected, setWsConnected] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<LogEntry[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOffset, setSearchOffset] = useState(0);
  const [searchHasMore, setSearchHasMore] = useState(false);

  // Jump to context (highlight a specific entry)
  const [highlightedEntryId, setHighlightedEntryId] = useState<string | null>(null);

  // Filters
  const [selectedLevels, setSelectedLevels] = useState<LogLevel[]>(['ERROR', 'WARN', 'INFO']);
  const [showFilters, setShowFilters] = useState(false);

  // Timezone preference (stored globally in localStorage)
  const [timezone, setTimezoneState] = useState(() =>
    localStorage.getItem('prepplogger_timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const setTimezone = (tz: string) => {
    setTimezoneState(tz);
    localStorage.setItem('prepplogger_timezone', tz);
  };

  // Date range
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');


  const logContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Load app status
  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [appName]);

  const loadStatus = async () => {
    try {
      const data = await getAppStatus(appName);
      setStatus(data);
    } catch (err) {
      console.error('Failed to load app status:', err);
    }
  };

  // Load recent logs from database on mount
  useEffect(() => {
    loadRecentLogs();
  }, [appName]);

  const loadRecentLogs = async () => {
    try {
      const result = await searchLogs(appName, {
        limit: 100,
        offset: 0,
      });
      // Set as initial live logs (newest first)
      setLiveLogs(result.entries);
      setLiveHasMore(result.hasMore);
    } catch (err) {
      console.error('Failed to load recent logs:', err);
    }
  };

  // Load older logs when scrolling down in live mode
  const loadOlderLogs = useCallback(async () => {
    if (liveLoading || !liveHasMore) return;

    setLiveLoading(true);
    try {
      const result = await searchLogs(appName, {
        limit: 100,
        offset: liveLogs.length,
      });

      if (result.entries.length > 0) {
        setLiveLogs((prev) => {
          // Merge and deduplicate by id
          const existingIds = new Set(prev.map(l => l.id));
          const newEntries = result.entries.filter(e => !existingIds.has(e.id));
          const merged = [...prev, ...newEntries];
          // Sort by timestamp (newest first)
          merged.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          return merged;
        });
      }
      setLiveHasMore(result.hasMore);
    } catch (err) {
      console.error('Failed to load older logs:', err);
    } finally {
      setLiveLoading(false);
    }
  }, [appName, liveLogs.length, liveLoading, liveHasMore]);

  // WebSocket connection for live logs
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 20;
  const shouldReconnectRef = useRef(true);

  const connect = useCallback(() => {
    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (!shouldReconnectRef.current || !isLiveMode) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/ws/logs`;
    console.log('Connecting to WebSocket:', wsUrl);

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected, subscribing to:', appName);
        setWsConnected(true);
        reconnectAttemptsRef.current = 0;
        ws.send(JSON.stringify({ type: 'subscribe', apps: [appName] }));

        // Start heartbeat to detect stale connections
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          } else {
            clearInterval(pingInterval);
            setWsConnected(false);
          }
        }, 30000); // Check every 30 seconds

        ws.addEventListener('close', () => clearInterval(pingInterval));
      };

      ws.onmessage = (event) => {
        if (isPaused) return;

        try {
          const message = JSON.parse(event.data);
          if (message.type === 'log' && message.app === appName) {
            const entry: LogEntry = {
              id: message.id,
              appName: message.app,
              timestamp: message.timestamp,
              level: message.level,
              message: message.message,
              raw: message.raw,
            };

            setLiveLogs((prev) => {
              // Add new entry and sort by timestamp (newest first)
              const newLogs = [entry, ...prev];
              newLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
              return newLogs.slice(0, 500);
            });
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        wsRef.current = null;

        if (shouldReconnectRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(1.5, reconnectAttemptsRef.current), 30000);
          console.log(`WebSocket closed, reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})`);
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        // Don't set disconnected here - onclose will be called if the connection actually closes
        // Setting it here causes false "disconnected" status when errors are transient
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setWsConnected(false);
    }
  }, [appName, isLiveMode, isPaused]);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    console.log('Manual reconnect triggered, current state:', {
      wsRef: wsRef.current?.readyState,
      shouldReconnect: shouldReconnectRef.current,
      isLiveMode,
    });
    reconnectAttemptsRef.current = 0;
    shouldReconnectRef.current = true;
    // Force close any existing connection before reconnecting
    if (wsRef.current) {
      wsRef.current.onclose = null; // Prevent auto-reconnect from onclose
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
    // Small delay to ensure clean slate
    setTimeout(() => connect(), 100);
  }, [connect, isLiveMode]);

  useEffect(() => {
    if (!isLiveMode) {
      shouldReconnectRef.current = false;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    shouldReconnectRef.current = true;
    connect();

    // Handle page visibility changes (wake from sleep/hibernation)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isLiveMode) {
        console.log('Page became visible, refreshing logs...');

        // Reload recent logs to get any we missed while sleeping
        loadRecentLogs();

        // Reconnect WebSocket if needed
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          console.log('WebSocket not connected, reconnecting...');
          reconnectAttemptsRef.current = 0;
          connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      shouldReconnectRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [appName, isLiveMode, connect]);

  // Auto-scroll - only scroll to top if user is already near the top
  useEffect(() => {
    if (autoScroll && logContainerRef.current && isLiveMode) {
      // Only auto-scroll if user is within 100px of the top
      if (logContainerRef.current.scrollTop < 100) {
        logContainerRef.current.scrollTop = 0;
      }
    }
  }, [liveLogs, autoScroll, isLiveMode]);

  // Check if there are more results to load (works for both live and search modes)
  const hasMore = useMemo(() => {
    if (isLiveMode) {
      return liveHasMore;
    }
    return searchHasMore;
  }, [isLiveMode, liveHasMore, searchHasMore]);

  // Loading state for infinite scroll
  const isLoadingMore = isLiveMode ? liveLoading : searchLoading;

  // Convert datetime-local value to ISO string
  const toISOString = (datetimeLocal: string): string | undefined => {
    if (!datetimeLocal) return undefined;
    try {
      // datetime-local format: "2026-01-28T14:30"
      const date = new Date(datetimeLocal);
      return date.toISOString();
    } catch {
      return undefined;
    }
  };

  // Search function
  const handleSearch = useCallback(async () => {
    setIsLiveMode(false);
    setSearchLoading(true);
    setSearchOffset(0);

    try {
      const result = await searchLogs(appName, {
        q: searchQuery || undefined,
        from: toISOString(dateFrom),
        to: toISOString(dateTo),
        level: selectedLevels.length > 0 ? selectedLevels : undefined,
        limit: 100,
        offset: 0,
      });

      setSearchResults(result.entries);

      setSearchHasMore(result.hasMore);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [appName, searchQuery, dateFrom, dateTo, selectedLevels]);

  const handleLoadMore = useCallback(async () => {
    if (searchLoading) return;

    const newOffset = searchOffset + 100;
    setSearchLoading(true);

    try {
      const result = await searchLogs(appName, {
        q: searchQuery || undefined,
        from: toISOString(dateFrom),
        to: toISOString(dateTo),
        level: selectedLevels.length > 0 ? selectedLevels : undefined,
        limit: 100,
        offset: newOffset,
      });

      setSearchResults((prev) => {
        const merged = [...prev, ...result.entries];

        return merged;
      });
      setSearchOffset(newOffset);
      setSearchHasMore(result.hasMore);
    } catch (err) {
      console.error('Load more failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [appName, searchQuery, dateFrom, dateTo, selectedLevels, searchOffset, searchLoading]);

  // Infinite scroll - load more when sentinel becomes visible (works for both modes)
  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          if (isLiveMode) {
            loadOlderLogs();
          } else {
            handleLoadMore();
          }
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, isLiveMode, handleLoadMore, loadOlderLogs]);

  const toggleLevel = (level: LogLevel) => {
    setSelectedLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const clearLogs = () => {
    setLiveLogs([]);
  };

  // Jump to context - load logs around a specific entry and highlight it
  const handleJumpToContext = useCallback(async (entry: LogEntry) => {
    if (!entry.id) return;

    setSearchLoading(true);

    try {
      // Load logs around the entry's timestamp (50 before, 50 after)
      const entryTime = new Date(entry.timestamp);

      // Get logs from a time range around the entry
      const from = new Date(entryTime.getTime() - 5 * 60 * 1000); // 5 minutes before
      const to = new Date(entryTime.getTime() + 5 * 60 * 1000); // 5 minutes after

      const result = await searchLogs(appName, {
        from: from.toISOString(),
        to: to.toISOString(),
        limit: 200,
        offset: 0,
      });

      // Clear search query so this is no longer treated as a search result view
      setSearchQuery('');

      // Switch to context view (search mode but not search results)
      setIsLiveMode(false);
      setSearchResults(result.entries);

      setSearchHasMore(result.hasMore);
      setSearchOffset(0);
      setHighlightedEntryId(entry.id);

      // Scroll to the entry after render
      setTimeout(() => {
        const element = document.getElementById(`log-entry-${entry.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Clear highlight after 3 seconds
        setTimeout(() => {
          setHighlightedEntryId(null);
        }, 3000);
      }, 100);
    } catch (err) {
      console.error('Failed to load context:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [appName]);

  // Display logs
  const displayLogs = isLiveMode ? liveLogs : searchResults;
  const filteredLogs = isLiveMode
    ? displayLogs.filter((log) => selectedLevels.length === 0 || selectedLevels.includes(log.level))
    : displayLogs;

  return (
    <div className="pane h-full">
      {/* Header */}
      <div className="pane-header">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Icons.Server className="w-4 h-4 text-blue-400" />
            <span className="font-semibold text-white">{appName}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`status-dot ${wsConnected ? 'status-connected' : 'status-disconnected'}`} />
            <span className="text-xs text-gray-400">
              {wsConnected ? 'Live' : 'Connecting...'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Live/Search toggle */}
          <button
            onClick={() => setIsLiveMode(!isLiveMode)}
            className={`btn text-xs py-1 px-3 ${
              isLiveMode ? 'bg-green-600/20 text-green-400 border border-green-600/30' : 'btn-secondary'
            }`}
            title={isLiveMode ? 'Switch to search mode' : 'Switch to live streaming mode'}
          >
            {isLiveMode && <span className="w-1.5 h-1.5 rounded-full bg-green-400 live-indicator" />}
            {isLiveMode ? 'Live' : 'Search'}
          </button>

          <button
            onClick={onClose}
            className="btn-ghost p-1.5 rounded-lg hover:text-red-400"
            title="Back to app list"
          >
            <Icons.X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* App Stats Bar */}
      {status?.stats && (
        <div className="px-4 py-2 flex items-center gap-4 text-xs border-b border-[var(--border-subtle)]" style={{ background: 'var(--bg-tertiary)' }}>
          {status.stats.oldestLog && (
            <div className="flex items-center gap-1.5">
              <Icons.Calendar className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-gray-400">From:</span>
              <span className="text-gray-200">{new Date(status.stats.oldestLog).toLocaleDateString()}</span>
            </div>
          )}
          {status.stats.newestLog && (
            <div className="flex items-center gap-1.5">
              <Icons.Calendar className="w-3.5 h-3.5 text-green-400" />
              <span className="text-gray-400">To:</span>
              <span className="text-gray-200">{new Date(status.stats.newestLog).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      )}

      {/* Search Bar and Level Filters */}
      <div className="p-3 border-b border-[var(--border-subtle)]" style={{ background: 'var(--bg-secondary)' }}>
        {/* Level filters - always visible */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-gray-400">Filter:</span>
          <div className="flex gap-1">
            {(['ERROR', 'WARN', 'INFO', 'DEBUG'] as LogLevel[]).map((level) => (
              <button
                key={level}
                onClick={() => toggleLevel(level)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  selectedLevels.includes(level)
                    ? LEVEL_BADGES[level]
                    : 'bg-gray-800/50 text-gray-500 hover:bg-gray-700 hover:text-gray-300'
                }`}
                title={selectedLevels.includes(level) ? `Hide ${level} logs` : `Show ${level} logs`}
              >
                {level}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              if (selectedLevels.length === 4) {
                setSelectedLevels([]);
              } else {
                setSelectedLevels(['ERROR', 'WARN', 'INFO', 'DEBUG']);
              }
            }}
            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
            title={selectedLevels.length === 4 ? 'Clear all level filters' : 'Select all log levels'}
          >
            {selectedLevels.length === 4 ? 'Clear' : 'All'}
          </button>
        </div>

        {/* Search input */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search logs..."
              className="input-modern text-sm pr-16"
            />
            {searchQuery ? (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setIsLiveMode(true);
                }}
                className="absolute right-8 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-red-600/20 text-gray-500 hover:text-red-400 transition-colors"
                title="Clear search and return to live view"
              >
                <Icons.X className="w-4 h-4" />
              </button>
            ) : null}
            <Icons.Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          </div>
          <button onClick={handleSearch} className="btn btn-primary text-sm py-2" title="Search historical logs">
            Search
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn text-sm py-2 ${showFilters ? 'btn-primary' : 'btn-secondary'}`}
            title={showFilters ? 'Hide date/time filters' : 'Show date/time filters'}
          >
            <Icons.Calendar className="w-4 h-4" />
          </button>
        </div>

        {/* Date Filters Panel */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-[var(--border-subtle)]">
            {/* Date range */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-400">From:</span>
              <div className="relative">
                <input
                  type="datetime-local"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="input-modern text-xs py-1.5 w-44 pr-7"
                  title="Start date and time for search range (optional)"
                />
                {dateFrom && (
                  <button
                    onClick={() => setDateFrom('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-red-600/20 text-gray-500 hover:text-red-400"
                    title="Clear from date"
                  >
                    <Icons.X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <span className="text-xs text-gray-400">To:</span>
              <div className="relative">
                <input
                  type="datetime-local"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="input-modern text-xs py-1.5 w-44 pr-7"
                  title="End date and time for search range (optional)"
                />
                {dateTo && (
                  <button
                    onClick={() => setDateTo('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-red-600/20 text-gray-500 hover:text-red-400"
                    title="Clear to date"
                  >
                    <Icons.X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                  }}
                  className="btn text-xs px-2 py-1 bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-600/30"
                  title="Clear all date filters"
                >
                  <Icons.X className="w-3 h-3" />
                  Reset Dates
                </button>
              )}
              <div className="flex gap-1 ml-2 pl-2 border-l border-[var(--border-subtle)]">
                {[
                  { label: '15m', hours: 0.25, tooltip: 'Show logs from the last 15 minutes' },
                  { label: '1h', hours: 1, tooltip: 'Show logs from the last hour' },
                  { label: '4h', hours: 4, tooltip: 'Show logs from the last 4 hours' },
                  { label: '24h', hours: 24, tooltip: 'Show logs from the last 24 hours' },
                  { label: '7d', hours: 24 * 7, tooltip: 'Show logs from the last 7 days' },
                  { label: '30d', hours: 24 * 30, tooltip: 'Show logs from the last 30 days' },
                ].map(({ label, hours, tooltip }) => (
                  <button
                    key={label}
                    onClick={() => {
                      const now = new Date();
                      const past = new Date(now.getTime() - hours * 60 * 60 * 1000);
                      // Format for datetime-local: YYYY-MM-DDTHH:MM
                      const formatLocal = (d: Date) => {
                        const pad = (n: number) => n.toString().padStart(2, '0');
                        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                      };
                      setDateFrom(formatLocal(past));
                      setDateTo(formatLocal(now));
                    }}
                    className="btn-ghost text-xs px-2 py-1 rounded"
                    title={tooltip}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="px-4 py-2 flex items-center justify-between text-xs border-b border-[var(--border-subtle)]" style={{ background: 'var(--bg-tertiary)' }}>
        <div className="flex items-center gap-4">
          {isLiveMode ? (
            <>
              <span className="text-gray-400">
                <span className="text-white font-medium">{filteredLogs.length}</span> logs
              </span>
              <div className="flex items-center gap-2">
                <span className={`status-dot ${wsConnected ? 'status-connected' : 'status-disconnected'}`} />
                <span className="text-gray-400">{wsConnected ? 'Streaming' : 'Disconnected'}</span>
                {!wsConnected && (
                  <button
                    onClick={reconnect}
                    className="px-2 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                    title="Reconnect to the live log stream"
                  >
                    Reconnect
                  </button>
                )}
              </div>
            </>
          ) : (
            <span className="text-gray-400">
              Showing <span className="text-white font-medium">{filteredLogs.length}</span> results
              {searchHasMore && ' (more available)'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isLiveMode && (
            <>
              <button
                onClick={() => setIsPaused(!isPaused)}
                className={`btn-ghost px-2 py-1 rounded text-xs flex items-center gap-1 ${
                  isPaused ? 'text-yellow-400' : ''
                }`}
                title={isPaused ? 'Resume receiving new logs' : 'Pause receiving new logs'}
              >
                {isPaused ? <Icons.Play className="w-3 h-3" /> : <Icons.Pause className="w-3 h-3" />}
                {isPaused ? 'Resume' : 'Pause'}
              </button>
              <button
                onClick={clearLogs}
                className="btn-ghost px-2 py-1 rounded text-xs flex items-center gap-1"
                title="Clear all logs from current view"
              >
                <Icons.Trash className="w-3 h-3" />
                Clear
              </button>
            </>
          )}
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={`btn-ghost px-2 py-1 rounded text-xs ${autoScroll ? 'text-blue-400' : ''}`}
            title={autoScroll ? 'Disable auto-scroll to top on new logs' : 'Enable auto-scroll to top on new logs'}
          >
            Auto-scroll: {autoScroll ? 'ON' : 'OFF'}
          </button>
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-[var(--border-subtle)]">
            <Icons.Clock className="w-3 h-3 text-gray-500" />
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="bg-transparent text-xs text-gray-400 hover:text-gray-200 cursor-pointer focus:outline-none"
              title="Select timezone for displaying timestamps"
            >
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value} className="bg-[var(--bg-elevated)]">
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Log Content */}
      <div
        ref={logContainerRef}
        className="flex-1 overflow-auto"
        style={{ background: 'var(--bg-primary)' }}
      >
        {filteredLogs.length === 0 && !searchLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <Icons.Database className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">No logs to display</p>
            <p className="text-xs mt-1 text-gray-600">
              {isLiveMode ? 'Waiting for new logs...' : 'Try adjusting your search filters'}
            </p>
          </div>
        ) : (
          <div className="p-2">
            {filteredLogs.map((log, index) => (
              <LogEntryRow
                key={log.id || `${log.timestamp}-${index}`}
                entry={log}
                isSearchResult={!isLiveMode && !!searchQuery}
                isHighlighted={log.id === highlightedEntryId}
                onJumpToContext={handleJumpToContext}
              />
            ))}

            {/* Infinite scroll sentinel - triggers load more when visible */}
            {hasMore && (
              <div
                ref={loadMoreRef}
                className="py-4 text-center"
              >
                {isLoadingMore ? (
                  <Icons.Spinner className="w-5 h-5 mx-auto text-blue-400" />
                ) : (
                  <span className="text-xs text-gray-500">Scroll for more...</span>
                )}
              </div>
            )}

            {/* Loading indicator when no more results */}
            {isLoadingMore && !hasMore && (
              <div className="py-4 text-center">
                <Icons.Spinner className="w-6 h-6 mx-auto text-blue-400" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
